<template>
  <section class="candidate-debug">
    <header class="candidate-debug__header">
      <div>
        <h2>Диагностика кандидатов</h2>
        <div class="candidate-debug__meta">
          <span>Состояние обхода: {{ lastScanLabel }}</span>
          <span>{{ state?.configuredArtefacts ?? 0 }} артефактов</span>
          <span>История: {{ state?.history.maxRecords ?? 1200 }} последних продаж</span>
        </div>
      </div>

      <Button
        icon="pi pi-refresh"
        text
        rounded
        aria-label="Обновить журнал"
        v-tooltip.bottom="'Обновить журнал'"
        :loading="loading"
        @click="loadCandidates"
      />
    </header>

    <div class="candidate-debug__summary">
      <div>
        <span>Активные</span>
        <strong>{{ activeCount }}</strong>
      </div>
      <div>
        <span>В основной таблице</span>
        <strong class="candidate-debug__success">{{ visibleCount }}</strong>
      </div>
      <div>
        <span>Скрыты фильтрами</span>
        <strong class="candidate-debug__warning">{{ hiddenCount }}</strong>
      </div>
      <div>
        <span>Исчезли</span>
        <strong>{{ disappearedCount }}</strong>
      </div>
    </div>

    <div class="candidate-debug__toolbar">
      <SelectButton
        v-model="mode"
        :options="modeOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
      />

      <span class="candidate-debug__search">
        <i class="pi pi-search" aria-hidden="true"></i>
        <InputText v-model="search" placeholder="Артефакт или ID" />
      </span>
    </div>

    <div v-if="error" class="candidate-debug__error">{{ error }}</div>

    <DataTable
      :value="filteredRecords"
      dataKey="id"
      paginator
      :rows="20"
      :rowsPerPageOptions="[20, 50, 100]"
      stripedRows
      responsiveLayout="scroll"
      class="candidate-debug__table"
    >
      <template #empty>Кандидатов в этом разделе пока нет</template>

      <Column header="Статус" style="width: 8.5rem">
        <template #body="{ data }">
          <Tag :value="statusLabel(data)" :severity="statusSeverity(data)" />
        </template>
      </Column>

      <Column field="name" header="Артефакт" sortable style="min-width: 10rem">
        <template #body="{ data }">
          <strong>{{ data.name }}</strong>
          <div class="candidate-debug__subtle">
            {{ data.artefactId }} · q{{ data.qlt }} · +{{ data.ptn }}
          </div>
        </template>
      </Column>

      <Column field="rawProfit" header="Профит" sortable style="min-width: 8rem">
        <template #body="{ data }">
          <strong :class="valueClass(data.rawProfit)">{{ formatPrice(data.rawProfit) }}</strong>
          <div class="candidate-debug__subtle">{{ data.roiPercent.toFixed(1) }}%</div>
        </template>
      </Column>

      <Column header="Цены" style="min-width: 11rem">
        <template #body="{ data }">
          <div>{{ formatPrice(data.currentPrice) }} → {{ formatPrice(data.salePrice) }}</div>
          <div class="candidate-debug__subtle">медиана {{ formatPrice(data.averagePrice) }}</div>
          <div v-if="data.calculatedSalePrice !== data.salePrice" class="candidate-debug__subtle">
            расчет {{ formatPrice(data.calculatedSalePrice) }}
          </div>
        </template>
      </Column>

      <Column field="sales24h" header="Продажи" sortable style="min-width: 7.5rem">
        <template #body="{ data }">
          <div>{{ data.sales24h }} / 24ч</div>
          <div class="candidate-debug__subtle">
            {{ data.sales48h }} / 48ч · {{ data.historyCount }} всего
          </div>
        </template>
      </Column>

      <Column header="Жизненный цикл" style="min-width: 11rem">
        <template #body="{ data }">
          <div>{{ formatDuration(data.firstSeen, data.disappearedAt) }}</div>
          <div class="candidate-debug__subtle">с {{ formatDate(data.firstSeen) }}</div>
          <div v-if="data.disappearedAt" class="candidate-debug__subtle">
            до {{ formatDate(data.disappearedAt) }}
          </div>
          <div class="candidate-debug__subtle">{{ data.observations }} проверок</div>
        </template>
      </Column>

      <Column header="Причины" style="min-width: 15rem">
        <template #body="{ data }">
          <div class="candidate-debug__tags">
            <Tag
              v-if="data.disappearanceReason"
              :value="disappearanceLabels[data.disappearanceReason]"
              severity="secondary"
            />
            <Tag
              v-for="reason in filterReasonLabels(data)"
              :key="reason"
              :value="reason"
              severity="warn"
            />
            <Tag
              v-if="!data.disappearanceReason && !filterReasonLabels(data).length"
              value="Все фильтры пройдены"
              severity="success"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </section>
</template>

<script setup lang="ts">
import { computed, defineProps, onBeforeUnmount, onMounted, ref, withDefaults } from 'vue'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import InputText from 'primevue/inputtext'
import SelectButton from 'primevue/selectbutton'
import Tag from 'primevue/tag'
import { api } from '@/api/http'
import { formatPrice } from '@/common/function'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  getArtefactFilterReasons,
  passesArtefactFilters
} from '@/common/artefact-filters'

type CandidateMode = 'active' | 'hidden' | 'disappeared'
type DisappearanceReason =
  | 'no_active_lot'
  | 'no_longer_profitable'
  | 'cheapest_lot_changed'
  | 'group_missing'

const props = withDefaults(
  defineProps<{
    onlyGoodLots?: boolean
    liquidityWindow?: LiquidityWindow
    minLiquiditySales?: number
    minBuyoutProfit?: number
  }>(),
  {
    onlyGoodLots: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid,
    liquidityWindow: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow,
    minLiquiditySales: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales,
    minBuyoutProfit: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit
  }
)

interface CandidateRecord {
  id: string
  artefactId: string
  name: string
  qlt: number
  ptn: string
  firstSeen: string
  lastSeen: string
  disappearedAt: string | null
  status: 'active' | 'disappeared'
  disappearanceReason: DisappearanceReason | null
  observations: number
  currentPrice: number
  salePrice: number
  calculatedSalePrice: number
  averagePrice: number
  rawProfit: number
  minRequiredProfit: number
  roiPercent: number
  historyCount: number
  sales24h: number
  sales48h: number
}

interface CandidateState {
  enabled: boolean
  updatedAt: string | null
  lastScanCompletedAt: string | null
  lastScanDurationSeconds: number | null
  configuredArtefacts: number
  scan: {
    inProgress: boolean
    startedAt: string | null
    processedArtefacts: number
    totalArtefacts: number
  }
  history: { pageSize: number; maxRecords: number; order: string }
  summary: { active: number; visible: number; hidden: number; disappeared: number }
  records: CandidateRecord[]
}

const modeOptions = [
  { label: 'Активные', value: 'active' },
  { label: 'Скрытые', value: 'hidden' },
  { label: 'Исчезнувшие', value: 'disappeared' }
]
const disappearanceLabels: Record<DisappearanceReason, string> = {
  no_active_lot: 'Лот снят или выкуплен',
  no_longer_profitable: 'Спред исчез',
  cheapest_lot_changed: 'Сменился дешевый лот',
  group_missing: 'Группа исчезла'
}
const state = ref<CandidateState | null>(null)
const mode = ref<CandidateMode>('active')
const search = ref('')
const loading = ref(false)
const error = ref('')
const now = ref(Date.now())
let timer: number | null = null
let secondsUntilRefresh = 0

const filterSettings = computed<ArtefactFilterSettings>(() => ({
  onlyLiquid: props.onlyGoodLots,
  liquidityWindow: props.liquidityWindow,
  minSales: props.minLiquiditySales,
  minProfit: props.minBuyoutProfit
}))
const activeRecords = computed(() =>
  (state.value?.records ?? []).filter(record => record.status === 'active')
)
const activeCount = computed(() => activeRecords.value.length)
const visibleCount = computed(
  () =>
    activeRecords.value.filter(record =>
      passesArtefactFilters(record, filterSettings.value, now.value)
    ).length
)
const hiddenCount = computed(() => activeCount.value - visibleCount.value)
const disappearedCount = computed(
  () => (state.value?.records ?? []).filter(record => record.status === 'disappeared').length
)

const filteredRecords = computed(() => {
  const query = search.value.toLocaleLowerCase('ru-RU')

  return (state.value?.records ?? [])
    .filter(record => {
      if (mode.value === 'active' && record.status !== 'active') return false
      if (
        mode.value === 'hidden' &&
        (record.status !== 'active' ||
          passesArtefactFilters(record, filterSettings.value, now.value))
      ) {
        return false
      }
      if (mode.value === 'disappeared' && record.status !== 'disappeared') return false

      return (
        !query || `${record.name} ${record.artefactId}`.toLocaleLowerCase('ru-RU').includes(query)
      )
    })
    .sort((a, b) => {
      if (a.status === 'active' && b.status === 'active') return b.rawProfit - a.rawProfit
      return (
        new Date(b.disappearedAt ?? b.lastSeen).getTime() -
        new Date(a.disappearedAt ?? a.lastSeen).getTime()
      )
    })
})

const lastScanLabel = computed(() => {
  if (state.value?.scan.inProgress) {
    const elapsed = state.value.scan.startedAt
      ? Math.max(0, now.value - new Date(state.value.scan.startedAt).getTime())
      : 0

    return `${state.value.scan.processedArtefacts}/${state.value.scan.totalArtefacts} · ${formatAgo(
      elapsed
    )}`
  }

  if (!state.value?.lastScanCompletedAt) return 'нет данных'

  const ago = Math.max(0, now.value - new Date(state.value.lastScanCompletedAt).getTime())
  return `${formatAgo(ago)} назад · ${state.value.lastScanDurationSeconds ?? 0}с`
})

async function loadCandidates() {
  if (loading.value) return

  loading.value = true
  error.value = ''

  try {
    const response = await api.get<CandidateState>('/artefact/debug/candidates')
    state.value = response.data
    secondsUntilRefresh = 10
  } catch {
    error.value = 'Не удалось загрузить журнал кандидатов'
  } finally {
    loading.value = false
  }
}

function statusLabel(record: CandidateRecord) {
  if (record.status === 'disappeared') return 'Исчез'
  return passesArtefactFilters(record, filterSettings.value, now.value) ? 'В таблице' : 'Скрыт'
}

function statusSeverity(record: CandidateRecord) {
  if (record.status === 'disappeared') return 'secondary'
  return passesArtefactFilters(record, filterSettings.value, now.value) ? 'success' : 'warn'
}

function filterReasonLabels(record: CandidateRecord): string[] {
  return getArtefactFilterReasons(record, filterSettings.value, now.value).map(reason => {
    if (reason === 'below_internal_profit') return 'Ниже внутреннего порога'
    if (reason === 'insufficient_history') return 'Мало истории'
    if (reason === 'below_configured_profit') {
      return `Профит < ${formatPrice(props.minBuyoutProfit)}`
    }
    if (reason === 'insufficient_liquidity') {
      return `Продаж < ${props.minLiquiditySales} за ${props.liquidityWindow}ч`
    }
    if (reason === 'no_recent_sale') return 'Нет продаж за 24ч'
    return 'Продажа не ниже медианы'
  })
}

function valueClass(value: number) {
  return value >= 0 ? 'candidate-debug__success' : 'candidate-debug__danger'
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(new Date(value))
}

function formatDuration(firstSeen: string, disappearedAt: string | null) {
  const end = disappearedAt ? new Date(disappearedAt).getTime() : now.value
  return formatAgo(Math.max(0, end - new Date(firstSeen).getTime()))
}

function formatAgo(milliseconds: number) {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours) return `${hours}ч ${minutes}м`
  if (minutes) return `${minutes}м ${seconds}с`
  return `${seconds}с`
}

onMounted(() => {
  void loadCandidates()
  timer = window.setInterval(() => {
    now.value = Date.now()
    secondsUntilRefresh -= 1
    if (secondsUntilRefresh <= 0) void loadCandidates()
  }, 1000)
})

onBeforeUnmount(() => {
  if (timer !== null) window.clearInterval(timer)
})
</script>

<style scoped lang="scss">
.candidate-debug {
  width: 95%;
  margin: 20px auto;
  padding: 18px 20px 20px;
  background: #242424;
  border: 1px solid #3d3d3d;
  border-radius: 6px;
  color: #eeeeee;
}

.candidate-debug__header,
.candidate-debug__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.candidate-debug__header h2 {
  margin: 0 0 5px;
  font-size: 18px;
  letter-spacing: 0;
}

.candidate-debug__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  color: #a8a8a8;
  font-size: 12px;
}

.candidate-debug__summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  margin: 16px 0;
  border-block: 1px solid #3d3d3d;
}

.candidate-debug__summary > div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-right: 1px solid #3d3d3d;
}

.candidate-debug__summary > div:last-child {
  border-right: 0;
}

.candidate-debug__summary span {
  color: #b8b8b8;
  font-size: 12px;
}

.candidate-debug__summary strong {
  font-size: 20px;
}

.candidate-debug__toolbar {
  margin-bottom: 14px;
}

.candidate-debug__search {
  position: relative;
  display: flex;
  align-items: center;
}

.candidate-debug__search i {
  position: absolute;
  left: 10px;
  z-index: 1;
  color: #8f8f8f;
}

.candidate-debug__search input {
  width: 220px;
  padding-left: 32px;
}

.candidate-debug__table {
  font-size: 13px;
}

.candidate-debug__subtle {
  margin-top: 3px;
  color: #999999;
  font-size: 11px;
  line-height: 1.35;
}

.candidate-debug__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.candidate-debug__success {
  color: #49c778;
}

.candidate-debug__warning {
  color: #e6b44a;
}

.candidate-debug__danger,
.candidate-debug__error {
  color: #f06c6c;
}

.candidate-debug__error {
  margin-bottom: 12px;
}

@media (max-width: 800px) {
  .candidate-debug {
    width: auto;
    margin: 12px 0;
    padding: 14px 10px;
  }

  .candidate-debug__summary {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .candidate-debug__summary > div:nth-child(2) {
    border-right: 0;
  }

  .candidate-debug__toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .candidate-debug__search input {
    width: 100%;
  }
}
</style>
